import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Box, Flex } from "@chakra-ui/react";
import { ytgRoutes } from "@modules/ytg";
import { gatewayRoutes } from "@modules/gateway";
import {
    NavBar,
    Footer,
    AuthListener,
    ProtectedRoute,
    AdminRoute,
    RoleGuard,
    WebSocketStatus,
    FullscreenSpinner,
} from "@shared/components";

// Lazy-load top-level pages so each one ships in its own webpack chunk.
const Home = lazy(() => import("@pages/Home"));
const NotFound = lazy(() => import("@pages/NotFound"));
const Dashboard = lazy(() => import("@pages/Dashboard"));
const Login = lazy(() => import("@pages/Login"));
const MentionsLegales = lazy(() => import("@pages/Legal/MentionsLegales"));
const Confidentialite = lazy(() => import("@pages/Legal/Confidentialite"));
const AdminHome = lazy(() => import("@pages/AdminHome"));
const StatsHome = lazy(() => import("@pages/StatsHome"));
const GlobalStats = lazy(() => import("@pages/GlobalStats"));
const YtgStats = lazy(() => import("@pages/YtgStats"));
const AdminOperations = lazy(() => import("@pages/AdminOperations"));
const AdminOperationDetail = lazy(() => import("@pages/AdminOperationDetail"));
const AdminUsers = lazy(() => import("@pages/AdminUsers"));
const AdminExtractionStats = lazy(() => import("@pages/AdminExtractionStats"));
const OutilsHome = lazy(() => import("@pages/OutilsHome"));
const ToolExtractor = lazy(() => import("@pages/ToolExtractor"));

/**
 * Application root: wires the router, global providers and the lazily-loaded
 * route tree (public pages, auth-guarded app pages and admin sections).
 */
const App = () => {
    return (
        <Router>
            <AuthListener />
            <Flex direction="column" minH="100vh">
                <NavBar/>
                <Box flex="1" display="flex" flexDirection="column">
                    <Suspense fallback={<FullscreenSpinner />}>
                        <Routes>
                            {/* Routes publiques */}
                            <Route path="/" element={<Home/>}/>
                            <Route path="/login" element={<Login/>}/>
                            <Route path="/mentions-legales" element={<MentionsLegales/>}/>
                            <Route path="/confidentialite" element={<Confidentialite/>}/>

                            {/* Routes protégées */}
                            <Route path="/app" element={<ProtectedRoute><Dashboard/></ProtectedRoute>}/>

                            {/* Outils — admin + tm */}
                            <Route path="/outils" element={<ProtectedRoute><RoleGuard requiredRoles={["admin", "tm"]}><OutilsHome/></RoleGuard></ProtectedRoute>}/>
                            <Route path="/outils/extracteur" element={<ProtectedRoute><RoleGuard requiredRoles={["admin", "tm"]}><ToolExtractor/></RoleGuard></ProtectedRoute>}/>

                            {/* Routes Admin - nécessitent d'être connecté ET admin */}
                            <Route path="/admin" element={<AdminRoute><AdminHome/></AdminRoute>}/>
                            <Route path="/admin/stats" element={<AdminRoute><StatsHome/></AdminRoute>}/>
                            <Route path="/admin/stats/global" element={<AdminRoute><GlobalStats/></AdminRoute>}/>
                            <Route path="/admin/stats/ytg" element={<AdminRoute><YtgStats/></AdminRoute>}/>
                            <Route path="/admin/ytg/operations" element={<AdminRoute><AdminOperations/></AdminRoute>}/>
                            <Route path="/admin/ytg/operation/:id" element={<AdminRoute><AdminOperationDetail/></AdminRoute>}/>
                            <Route path="/admin/stats/extraction" element={<AdminRoute><AdminExtractionStats/></AdminRoute>}/>
                            <Route path="/admin/users" element={<AdminRoute><AdminUsers/></AdminRoute>}/>

                            {/* Routes YTG (déjà protégées dans ytgRoutes) */}
                            {ytgRoutes}

                            {/* Routes Gateway */}
                            {gatewayRoutes}

                            {/* 404 protégée - redirige vers login si pas connecté, sinon affiche 404 */}
                            <Route path="*" element={<ProtectedRoute><NotFound/></ProtectedRoute>}/>
                        </Routes>
                    </Suspense>
                </Box>
                <Footer />
                <WebSocketStatus />
            </Flex>
        </Router>
    );
}

export default App;
